import { z } from 'zod';
const uuid = z.string().uuid();
const words = (max) => z.string().trim().min(1).max(max);
const link = z.string().url().startsWith('https://').max(500);
const when = z.string().datetime({ offset: true });
export const institutionCreateSchema = z
    .object({
    key: uuid,
    kind: z.enum(['accelerator', 'investor', 'university']),
    institutionName: words(200),
    programmeName: words(200).nullish(),
    cohort: words(100).nullish(),
    goal: words(1000),
    officialUrl: link,
    deadlineAt: when.nullish(),
    timeZone: words(100).default('Europe/Riga'),
    fundingId: uuid.nullish(),
    companyId: uuid.nullish(),
    ownerHuman: uuid,
    leadAgent: uuid.nullish(),
    twinTaskId: uuid.nullish(),
})
    .refine((v) => !v.leadAgent || !v.twinTaskId, {
    path: ['twinTaskId'],
    message: 'Choose either an assigned research agent or a confirmed twin task.',
})
    .refine((v) => {
    try {
        new Intl.DateTimeFormat('en', { timeZone: v.timeZone });
        return true;
    }
    catch {
        return false;
    }
}, { path: ['timeZone'], message: 'Use an IANA time zone such as Europe/Riga.' });
export const institutionAssessmentSchema = z
    .object({
    key: uuid,
    programmeId: uuid,
    sourceUrl: link,
    researchNoteId: uuid.nullish(),
    checkedAt: when,
    refreshAt: when,
    fit: z.enum(['unknown', 'eligible', 'ineligible', 'needs_review']),
    eligibility: words(2000),
    costs: words(1000),
    equity: words(1000),
    intellectualProperty: words(1000),
    uncertainty: words(1000),
    recommendation: words(1000),
})
    .refine((v) => Date.parse(v.refreshAt) > Date.parse(v.checkedAt), {
    path: ['refreshAt'],
    message: 'The next source check must follow this check.',
})
    .refine((v) => Date.parse(v.checkedAt) <= Date.now() + 300_000, {
    path: ['checkedAt'],
    message: 'The source check cannot be in the future.',
});
export const institutionPortalReviewSchema = z
    .object({
    key: uuid,
    programmeId: uuid,
    sourceUrl: link,
    portalUrl: link,
    checkedAt: when,
    validUntil: when,
    draftSupported: z.boolean(),
    personalLoginRequired: z.boolean(),
    signatureRequired: z.boolean(),
    paymentRequired: z.boolean(),
    termsConsentRequired: z.boolean(),
})
    .refine((v) => Date.parse(v.validUntil) > Date.parse(v.checkedAt), {
    path: ['validUntil'],
    message: 'Set a future date to recheck the portal capabilities.',
})
    .refine((v) => Date.parse(v.checkedAt) <= Date.now() + 300_000, {
    path: ['checkedAt'],
    message: 'The portal check cannot be in the future.',
});
export const institutionContactPathSchema = z
    .object({
    key: uuid,
    programmeId: uuid,
    contactId: uuid.nullish(),
    roleTitle: words(200),
    routeKind: z.enum(['public_form', 'warm_introduction', 'existing_relationship', 'unknown']),
    sourceUrl: link,
    checkedAt: when,
    state: z.enum(['research_candidate', 'verified', 'discarded']),
})
    .refine((v) => Date.parse(v.checkedAt) <= Date.now() + 300_000, {
    path: ['checkedAt'],
    message: 'The source check cannot be in the future.',
});
export const institutionRequirementSchema = z
    .object({
    key: uuid,
    programmeId: uuid,
    requirementKey: uuid.nullish(),
    wording: words(2000),
    sourceUrl: link,
    observedAt: when,
    dueAt: when.nullish(),
    ownerHuman: uuid.nullish(),
    state: z
        .enum(['unknown', 'needed', 'in_progress', 'ready', 'blocked', 'not_needed'])
        .default('needed'),
    evidenceRef: words(500).nullish(),
    taskId: uuid.nullish(),
    requestId: uuid.nullish(),
})
    .refine((v) => Date.parse(v.observedAt) <= Date.now() + 300_000, {
    path: ['observedAt'],
    message: 'The source observation cannot be in the future.',
})
    .refine((v) => v.state === 'not_needed' || Boolean(v.dueAt), {
    path: ['dueAt'],
    message: 'An open or ready requirement needs a review due date.',
})
    .refine((v) => v.state !== 'ready' || Boolean(v.evidenceRef), {
    path: ['evidenceRef'],
    message: 'A ready requirement needs an evidence reference.',
});
export const institutionPackageSchema = z.object({
    key: uuid,
    programmeId: uuid,
    answers: z
        .array(z.object({
        requirementKey: uuid,
        requirementVersion: z.number().int().positive(),
        answer: words(8000),
        sources: z.array(link).min(1).max(20),
    }))
        .max(100),
    items: z
        .array(z.object({
        title: words(200),
        sourceUrl: link,
        version: words(100),
        confidentiality: z.enum(['public', 'internal', 'restricted', 'personal']),
        validUntil: when.nullish(),
        disclosure: z.enum(['approved', 'needs_review', 'restricted']),
    }))
        .max(100),
});
export const institutionPackageReviewSchema = z.object({
    destination: link.optional(),
    packageId: uuid,
    decision: z.enum(['approved', 'rejected']),
    disclosureReviewed: z.boolean(),
    reason: words(1000),
});
export const institutionSubmissionSchema = z
    .object({
    key: uuid,
    programmeId: uuid,
    packageId: uuid,
    destination: link.optional(),
    channel: z.enum(['portal', 'email', 'introduction', 'manual']),
    portalUrl: link.nullish(),
    submittedAt: when,
    confirmationRef: words(500),
})
    .refine((v) => Date.parse(v.submittedAt) <= Date.now() + 300_000, {
    path: ['submittedAt'],
    message: 'Record an actual submission time, not a future plan.',
})
    .refine((v) => v.channel !== 'portal' || Boolean(v.portalUrl), {
    path: ['portalUrl'],
    message: 'Name the exact portal URL used for a portal submission.',
})
    .refine((v) => v.channel === 'portal' || !v.portalUrl, {
    path: ['portalUrl'],
    message: 'Only a portal submission names a portal URL.',
});
export const institutionInteractionSchema = z
    .object({
    key: uuid,
    programmeId: uuid,
    kind: z.enum(['meeting', 'call', 'email', 'introduction', 'interview', 'portal', 'note']),
    occurredAt: when,
    sourceRef: words(500),
    manualCommunicationId: uuid.nullish(),
    communicationEventId: uuid.nullish(),
    calendarEventId: uuid.nullish(),
    nativeMessageId: uuid.nullish(),
    outcome: words(2000),
    nextAction: words(500).nullish(),
    reviewAt: when.nullish(),
})
    .refine((v) => Boolean(v.nextAction) === Boolean(v.reviewAt), {
    path: ['reviewAt'],
    message: 'A next action needs its review time, and a review time needs an action.',
})
    .refine((v) => [
    v.manualCommunicationId,
    v.communicationEventId,
    v.calendarEventId,
    v.nativeMessageId,
].filter(Boolean).length <= 1, {
    path: ['communicationEventId'],
    message: 'Link one original communication source at a time.',
})
    .refine((v) => Date.parse(v.occurredAt) <= Date.now() + 300_000, {
    path: ['occurredAt'],
    message: 'Record an actual interaction time, not a future plan.',
});
export const institutionDecisionSchema = z.object({
    programmeId: uuid,
    decision: z.enum(['accepted', 'declined', 'withdrawn', 'interview', 'decision_waiting']),
    evidence: words(1000),
});
/** Agent output is a proposal only; ownership, state and authority are never
 * model-supplied. A human creates only needed requirements from it. */
export const institutionResearchProposalSchema = z
    .object({
    requirements: z
        .array(z
        .object({
        wording: words(2000),
        sourceUrl: link,
        observedAt: when,
        dueAt: when,
    })
        .strict())
        .max(20),
    followups: z
        .array(z
        .object({
        title: words(200),
        reason: words(1000),
        sourceUrl: link,
        dueAt: when,
    })
        .strict())
        .max(10),
})
    .strict()
    .refine((v) => v.requirements.length + v.followups.length > 0, {
    message: 'Propose at least one sourced requirement or follow-up.',
})
    .refine((v) => v.requirements.every((r) => Date.parse(r.observedAt) <= Date.now() + 300_000), {
    message: 'Source observations cannot be in the future.',
});
export const institutionProposalCaptureSchema = z
    .object({ programmeId: uuid, noteId: uuid })
    .strict();
export const institutionProposalDecisionSchema = z
    .object({ proposalId: uuid, decision: z.enum(['accepted', 'rejected']) })
    .strict();
//# sourceMappingURL=institution-work.js.map